import React, { useEffect, useState } from "react";
import {
  listOrders,
  createOrder,
  updateOrder,
  deleteOrder,
} from "../../lib/adminApi";
import { plateLookup, searchParts, fetchSuppliers } from "../../lib/api";
import {
  Loader2,
  Plus,
  ExternalLink,
  Trash2,
  Package,
  Search,
  ShoppingBag,
  CheckCircle2,
  Clock,
  Hourglass,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const STATUT = {
  a_commander: { label: "À commander", color: "border-amber-500/40 bg-amber-500/10 text-amber-300", icon: Hourglass },
  commandee: { label: "Commandée", color: "border-blue-500/40 bg-blue-500/10 text-blue-300", icon: Clock },
  recue: { label: "Reçue", color: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", icon: CheckCircle2 },
  annulee: { label: "Annulée", color: "border-slate-700 bg-slate-800 text-slate-400", icon: Trash2 },
};

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Barre de recherche rapide par plaque
  const [plate, setPlate] = useState("");
  const [part, setPart] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [offers, setOffers] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listOrders();
      setOrders(data.orders || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLookup = async () => {
    if (!plate.trim()) return;
    setSearching(true);
    setOffers([]);
    try {
      const d = await plateLookup(plate.trim());
      setVehicle(d.vehicle);
      toast.success(`${d.vehicle.marque} ${d.vehicle.modele} identifié`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Plaque introuvable");
      setVehicle(null);
    } finally {
      setSearching(false);
    }
  };

  const handlePartSearch = async () => {
    if (!vehicle || !part.trim()) return;
    setSearching(true);
    try {
      const d = await searchParts({ vehicle, part_query: part.trim() });
      setOffers(d.offers || []);
    } catch (err) {
      toast.error("Erreur de recherche");
    } finally {
      setSearching(false);
    }
  };

  const addToOrders = async (offer) => {
    try {
      await createOrder({
        plaque: vehicle?.plaque,
        marque: vehicle?.marque,
        modele: vehicle?.modele,
        annee: vehicle?.annee,
        motorisation: vehicle?.motorisation,
        part_label: offer?.part_label || part,
        reference_oem: offer?.reference_oem,
        supplier_id: offer?.supplier_id,
        supplier_name: offer?.supplier_name,
        supplier_url: offer?.affiliate_url,
        prix: offer?.prix_total,
        statut: "a_commander",
      });
      toast.success("Ajouté à ton carnet");
      await load();
    } catch (err) {
      toast.error("Impossible d'ajouter la commande");
    }
  };

  const changeStatut = async (id, statut) => {
    await updateOrder(id, { statut });
    toast.success("Statut mis à jour");
    await load();
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette entrée ?")) return;
    await deleteOrder(id);
    toast.success("Entrée supprimée");
    await load();
  };

  const filtered =
    filter === "all" ? orders : orders.filter((o) => o.statut === filter);

  return (
    <div data-testid="admin-orders-page" className="space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-50 sm:text-3xl">
          Mes commandes
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-400">
          Carnet personnel : recherche une pièce par plaque, ajoute-la à ta liste,
          et suis manuellement l'avancement. L'achat se fait toujours chez le
          fournisseur — ce carnet est juste un raccourci pour toi.
        </p>
      </header>

      {/* Recherche rapide */}
      <section className="surface-card rounded-2xl p-5 sm:p-6">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-slate-50">
          <Search className="h-4 w-4 text-cyan-400" />
          Recherche rapide d'une pièce
        </h2>
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Input
            data-testid="orders-plate-input"
            value={plate}
            onChange={(e) => setPlate(e.target.value.toUpperCase())}
            placeholder="Plaque (ex. AA-123-AA)"
            className="border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus-visible:ring-cyan-500"
          />
          <Button
            data-testid="orders-plate-submit"
            onClick={handleLookup}
            disabled={searching || !plate.trim()}
            className="gap-2 bg-cyan-500 text-slate-950 hover:bg-cyan-400"
          >
            {searching && !vehicle ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Identifier
          </Button>
          <div />
        </div>
        {vehicle && (
          <div
            data-testid="orders-vehicle-info"
            className="mt-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4"
          >
            <p className="text-sm text-slate-200">
              <strong className="text-cyan-200">{vehicle.marque} {vehicle.modele}</strong>{" "}
              — {vehicle.annee} · {vehicle.motorisation}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
              <Input
                data-testid="orders-part-input"
                value={part}
                onChange={(e) => setPart(e.target.value)}
                placeholder="Nom de la pièce recherchée..."
                className="border-slate-700 bg-slate-900/60 text-slate-100 focus-visible:ring-cyan-500"
              />
              <Button
                data-testid="orders-part-submit"
                onClick={handlePartSearch}
                disabled={searching || !part.trim()}
                className="gap-2 bg-cyan-500 text-slate-950 hover:bg-cyan-400"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                Comparer
              </Button>
            </div>
          </div>
        )}
        {offers.length > 0 && (
          <div className="mt-4 space-y-2">
            {offers.slice(0, 6).map((o, i) => (
              <div
                key={o.supplier_id}
                data-testid={`orders-offer-${o.supplier_id}`}
                className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="h-7 w-7 rounded flex items-center justify-center font-heading text-xs font-black text-white" style={{ background: o.supplier_couleur }}>
                    {o.supplier_logo_initial}
                  </span>
                  <div>
                    <div className="font-semibold text-slate-100">{o.supplier_name}</div>
                    <div className="text-xs text-slate-500">
                      {o.prix_total.toFixed(2)} € · {o.delai_livraison}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={o.affiliate_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-200 hover:border-cyan-500/40 hover:text-cyan-300"
                  >
                    Site <ExternalLink className="inline h-3 w-3" />
                  </a>
                  <Button
                    size="sm"
                    data-testid={`orders-add-${o.supplier_id}`}
                    onClick={() => addToOrders(o)}
                    className="gap-1 bg-emerald-500 text-emerald-950 hover:bg-emerald-400"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Ajouter
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Liste des commandes */}
      <section className="surface-card rounded-2xl p-5 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-heading text-base font-bold text-slate-50">
            <ShoppingBag className="h-4 w-4 text-cyan-400" />
            Mon carnet ({orders.length})
          </h2>
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-800 bg-slate-900/70 p-1">
            {[
              { id: "all", label: "Tous" },
              ...Object.entries(STATUT).map(([k, v]) => ({ id: k, label: v.label })),
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`orders-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  filter === f.id
                    ? "bg-cyan-500 text-slate-950"
                    : "text-slate-300 hover:text-cyan-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Chargement...
          </div>
        ) : filtered.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500">
            {orders.length === 0
              ? "Aucune commande encore. Lance une recherche ci-dessus et ajoute une pièce."
              : "Rien dans ce filtre."}
          </p>
        ) : (
          <ul className="space-y-2">
            {filtered.map((o) => {
              const st = STATUT[o.statut] || STATUT.a_commander;
              const StIcon = st.icon;
              return (
                <li
                  key={o.id}
                  data-testid={`order-row-${o.id}`}
                  className="flex flex-col gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-100">{o.part_label}</span>
                      {o.plaque && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-950">
                          {o.plaque}
                        </span>
                      )}
                      <Badge variant="outline" className={`text-[10px] ${st.color}`}>
                        <StIcon className="mr-1 h-3 w-3" />
                        {st.label}
                      </Badge>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {o.marque} {o.modele} · {o.supplier_name}
                      {o.prix ? ` · ${o.prix.toFixed(2)} €` : ""}
                      {o.reference_oem ? ` · ${o.reference_oem}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={o.statut} onValueChange={(v) => changeStatut(o.id, v)}>
                      <SelectTrigger data-testid={`order-statut-trigger-${o.id}`} className="h-8 w-36 border-slate-700 bg-slate-900 text-xs text-slate-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUT).map(([k, v]) => (
                          <SelectItem key={k} value={k}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {o.supplier_url && (
                      <a
                        href={o.supplier_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1 text-xs text-slate-200 hover:border-cyan-500/40 hover:text-cyan-300"
                      >
                        Site <ExternalLink className="inline h-3 w-3" />
                      </a>
                    )}
                    <button
                      data-testid={`order-delete-${o.id}`}
                      onClick={() => remove(o.id)}
                      className="rounded-md border border-slate-700 bg-slate-900 p-1.5 text-slate-400 hover:border-red-500/40 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
